import React, { useMemo } from 'react';
import {
  NavigateFunction,
  Params,
  To,
  useInRouterContext,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { Action } from 'history';
import { parseQuery, toNavigateTarget, ToTarget } from './link_parser';

export interface WithRouterLocation {
  pathname: string;
  search: string;
  hash: string;
  state: any;
  key: string;
  // Not available from v7's useLocation(); stubbed for compatibility with call
  // sites (e.g. the 'history' package's Location type) that still require it.
  action: Action;
  // v3-style parsed query object, kept for compatibility with existing call sites.
  query: Record<string, any>;
}

export interface WithRouterRouter {
  push: (to: To | ToTarget) => void;
  replace: (to: To | ToTarget) => void;
}

export interface WithRouterProps {
  router: WithRouterRouter;
  location: WithRouterLocation;
  params: Readonly<Params<string>>;
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
}

interface InnerProps<ExternalProps> {
  component: React.ComponentType<ExternalProps & WithRouterProps>;
  externalProps: ExternalProps;
}

// rr3 parity: withRouter is a no-op outside a router; the injected props come
// through undefined and consumers guard, same as rr3.
function RouterlessBacked<ExternalProps>({ component: Component, externalProps }: InnerProps<ExternalProps>) {
  return <Component {...externalProps} {...({} as WithRouterProps)} />;
}

function RouterBacked<ExternalProps>({ component: Component, externalProps }: InnerProps<ExternalProps>) {
  const rrLocation = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const location = useMemo<WithRouterLocation>(() => ({
    pathname: rrLocation.pathname,
    search: rrLocation.search,
    hash: rrLocation.hash,
    state: rrLocation.state,
    key: rrLocation.key,
    action: 'PUSH' as Action,
    query: parseQuery(rrLocation.search),
  }), [rrLocation]);

  const router = useMemo<WithRouterRouter>(() => ({
    // v3 call sites pass { pathname, query } descriptors; v7's navigate
    // silently ignores `query`, so serialize through toNavigateTarget.
    push: (to) => navigate(toNavigateTarget(to as ToTarget)),
    replace: (to) => navigate(toNavigateTarget(to as ToTarget), { replace: true }),
  }), [navigate]);

  return (
    <Component
      {...externalProps}
      router={router}
      location={location}
      params={params}
      navigate={navigate}
      searchParams={searchParams}
    />
  );
}

export function withRouter<ExternalProps>(
  Component: React.ComponentType<ExternalProps & WithRouterProps>,
) {
  return function ComponentWithRouter(props: ExternalProps) {
    if (useInRouterContext()) {
      return <RouterBacked component={Component} externalProps={props} />;
    }
    return <RouterlessBacked component={Component} externalProps={props} />;
  };
}
